import {
  inferRunReportsVisualizationMappingFromFields,
  resolveRunReportsVisualizationMappingByTemplateId,
  type RunReportsTemplateVisualizationMapping,
} from "@triedge/domain-core/report-generation";
import type {
  ReportGenerationAdvancedVisualizationChartColumn,
  ReportGenerationAdvancedVisualizationBodySurface,
  ReportGenerationAdvancedVisualizationChartOption,
  ReportGenerationAdvancedVisualizationChartPoint,
  ReportGenerationAdvancedVisualizationChartSnapshot,
  ReportGenerationAdvancedVisualizationChartType,
  ReportGenerationAdvancedVisualizationEntityLink,
  ReportGenerationAdvancedVisualizationPolicy,
  ReportGenerationVisualizationPolicyFallbackReason,
  ReportGenerationVisualizationPolicyRuleId,
} from "./types";

export const REPORT_GENERATION_VISUALIZATION_POLICY_ID =
  "ai-assistant-ui.report-generation.visualization.v1";

export const REPORT_GENERATION_VISUALIZATION_POLICY_CONFIG = Object.freeze({
  graphViewEnabled: false,
});

export const REPORT_GENERATION_DEFAULT_CHART_TYPE_PRIORITY = [
  "bar",
  "line",
  "pie",
  "scatter",
] as const satisfies readonly ReportGenerationAdvancedVisualizationChartType[];

const CHART_SERIES_COLORS = [
  "#2697A2",
  "#237062",
  "#08BFAE",
  "#C57A00",
  "#7C3AED",
  "#CC4444",
];

export type ReportGenerationVisualizationPolicyEngineRow = {
  cells: Record<string, unknown>;
};

export type ReportGenerationVisualizationPolicyEngineInput = {
  columns: readonly ReportGenerationAdvancedVisualizationChartColumn[];
  entityLinks: readonly ReportGenerationAdvancedVisualizationEntityLink[];
  rows: readonly ReportGenerationVisualizationPolicyEngineRow[];
  warnings?: readonly string[];
  templateId?: string | null;
  selectedFieldSignature?: string;
  filterSignature?: string;
};

export type ReportGenerationVisualizationPolicyResult =
  ReportGenerationAdvancedVisualizationChartSnapshot & {
    policy: ReportGenerationAdvancedVisualizationPolicy;
  };

export type ReportGenerationVisualizationPolicyValidationIssue = {
  code:
    | "graph-view-disabled"
    | "graph-default-disallowed"
    | "missing-chart-option"
    | "missing-table-fallback"
    | "degenerate-pie-option"
    | "degenerate-scatter-option"
    | "incoherent-default-chart"
    | "incoherent-default-body-surface"
    | "incoherent-available-chart-types";
  message: string;
};

const sortedUnique = (values: readonly unknown[]): string[] =>
  [
    ...new Set(
      values.map((value) => String(value ?? "").trim()).filter(Boolean),
    ),
  ].sort();

const normalizeKeyPart = (value: unknown): string =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._:-]+/g, "-")
    .replace(/^-+|-+$/g, "") || "unknown";

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const readNumericValue = (value: unknown): number | null => {
  if (isFiniteNumber(value)) {
    return value;
  }
  if (typeof value !== "string") {
    return null;
  }
  const parsed = Number(value.replace(/[$,%\s,]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const readIsoMonthBucket = (value: unknown): string | null => {
  const text = String(value ?? "").trim();
  if (!text) {
    return null;
  }
  const match = text.match(/^(\d{4})-(\d{2})/);
  if (match) {
    return `${match[1]}-${match[2]}`;
  }
  const time = Date.parse(text);
  if (!Number.isFinite(time)) {
    return null;
  }
  return new Date(time).toISOString().slice(0, 7);
};

const UNSAFE_TEXT_LABEL_PATTERN =
  /\b(family|student|parent|guardian|contact|name|email|phone|address|description|reference|allocated)\b/i;
const SAFE_TEXT_LABEL_PATTERN =
  /\b(status|category|grade|division|method|type|year|plan|source)\b/i;
const GROUP_DIMENSION_FIELD_IDS = [
  "payment_method",
  "payment_status",
  "payment_status_p",
  "entry_type",
  "contract_status",
  "account_status",
  "academic_year",
  "plan_name",
  "is_overdue",
];

const COMPLEX_TABLE_FIRST_MIN_COLUMNS = 12;
const COMPLEX_TABLE_FIRST_MIN_ROWS = 50;
const COMPLEX_TABLE_FIRST_MIN_ENTITIES = 3;
const COMPLEX_TABLE_FIRST_MIN_CATEGORIES = 3;

type CategoryGroupConcept = {
  bucket: ReportGenerationAdvancedVisualizationChartColumn;
  group: ReportGenerationAdvancedVisualizationChartColumn;
};

const isSafeTextCategoryColumn = (
  column: ReportGenerationAdvancedVisualizationChartColumn,
): boolean => {
  const text = `${column.fieldId} ${column.label}`;
  return (
    !UNSAFE_TEXT_LABEL_PATTERN.test(text) && SAFE_TEXT_LABEL_PATTERN.test(text)
  );
};

const isCategoryColumn = (
  column: ReportGenerationAdvancedVisualizationChartColumn,
): boolean =>
  column.fieldType === "enum" ||
  column.fieldType === "boolean" ||
  (column.fieldType === "text" && isSafeTextCategoryColumn(column));

const readCategoryBucket = (
  value: unknown,
  column: ReportGenerationAdvancedVisualizationChartColumn,
): string | null => {
  if (column.fieldType === "boolean") {
    return typeof value === "boolean" ? (value ? "Yes" : "No") : null;
  }
  if (!isCategoryColumn(column)) {
    return null;
  }
  const text = String(value ?? "").trim();
  if (
    !text ||
    UNSAFE_TEXT_LABEL_PATTERN.test(`${column.fieldId} ${column.label}`)
  ) {
    return null;
  }
  return text.slice(0, 48);
};

const categoryDistinctBucketCount = (input: {
  rows: readonly ReportGenerationVisualizationPolicyEngineRow[];
  column: ReportGenerationAdvancedVisualizationChartColumn;
}): number =>
  new Set(
    input.rows
      .map((row) =>
        readCategoryBucket(row.cells[input.column.fieldId], input.column),
      )
      .filter((bucket): bucket is string => Boolean(bucket)),
  ).size;

const hasUsefulBucketShape = (
  points: readonly ReportGenerationAdvancedVisualizationChartPoint[],
): boolean =>
  new Set(
    points
      .map((point) => String(point.bucket ?? point.label ?? point.key).trim())
      .filter((label) => label && label.toLowerCase() !== "all rows"),
  ).size >= 2;

const hasUsefulScatterShape = (
  points: readonly ReportGenerationAdvancedVisualizationChartPoint[],
): boolean => {
  const rowLevelPairs = points.filter(
    (point) =>
      typeof point.x === "number" &&
      Number.isFinite(point.x) &&
      typeof point.y === "number" &&
      Number.isFinite(point.y),
  );
  return (
    rowLevelPairs.length >= 2 &&
    new Set(rowLevelPairs.map((point) => `${point.x}:${point.y}`)).size >= 2
  );
};

const hasUsefulSingleSeriesShape = (
  points: readonly ReportGenerationAdvancedVisualizationChartPoint[],
): boolean => {
  if (points.length >= 2) {
    return true;
  }
  return hasUsefulBucketShape(points);
};

const distinctEntityCount = (input: {
  columns: readonly ReportGenerationAdvancedVisualizationChartColumn[];
  entityLinks: readonly ReportGenerationAdvancedVisualizationEntityLink[];
}): number =>
  new Set([
    ...input.columns
      .map((column) => column.entityId)
      .filter((entityId): entityId is string => Boolean(entityId)),
    ...input.entityLinks
      .flatMap((link) => [link.leftEntityId, link.rightEntityId])
      .filter((entityId): entityId is string => Boolean(entityId)),
  ]).size;

const shouldUseTableFirstForComplexSelection = (input: {
  rows: readonly ReportGenerationVisualizationPolicyEngineRow[];
  columns: readonly ReportGenerationAdvancedVisualizationChartColumn[];
  entityLinks: readonly ReportGenerationAdvancedVisualizationEntityLink[];
  categoryColumns: readonly ReportGenerationAdvancedVisualizationChartColumn[];
}): boolean =>
  input.columns.length >= COMPLEX_TABLE_FIRST_MIN_COLUMNS &&
  input.rows.length >= COMPLEX_TABLE_FIRST_MIN_ROWS &&
  distinctEntityCount({
    columns: input.columns,
    entityLinks: input.entityLinks,
  }) >= COMPLEX_TABLE_FIRST_MIN_ENTITIES &&
  input.categoryColumns.length >= COMPLEX_TABLE_FIRST_MIN_CATEGORIES;

const categoryGroupConceptScore = (
  column: ReportGenerationAdvancedVisualizationChartColumn,
): number => {
  const text = `${column.fieldId} ${column.label}`;
  const configuredIndex = GROUP_DIMENSION_FIELD_IDS.indexOf(column.fieldId);
  let score = configuredIndex >= 0 ? 120 - configuredIndex : 0;
  if (/\bacademic[-_\s]?year\b|\byear\b/i.test(text)) score += 100;
  if (/\bstatus\b/i.test(text)) score += 70;
  if (/\bmethod\b/i.test(text)) score += 55;
  if (/\bplan\b/i.test(text)) score += 45;
  if (/\bgrade\b/i.test(text)) score -= 30;
  return score;
};

const categoryBucketConceptScore = (
  column: ReportGenerationAdvancedVisualizationChartColumn,
): number => {
  const text = `${column.fieldId} ${column.label}`;
  let score = 20;
  if (/\b(enrolled[-_\s]?grade|grade)\b/i.test(text)) score += 100;
  if (/\b(plan|type|category|division)\b/i.test(text)) score += 65;
  if (/\bstatus\b/i.test(text)) score += 45;
  if (/\bacademic[-_\s]?year\b|\byear\b/i.test(text)) score -= 40;
  return score;
};

const chooseCategoryGroupConcept = (input: {
  rows: readonly ReportGenerationVisualizationPolicyEngineRow[];
  categoryColumns: readonly ReportGenerationAdvancedVisualizationChartColumn[];
}): CategoryGroupConcept | null => {
  if (input.categoryColumns.length < 2) {
    return null;
  }
  const candidates = input.categoryColumns
    .map((column, index) => ({
      column,
      index,
      distinctBucketCount: categoryDistinctBucketCount({
        rows: input.rows,
        column,
      }),
      groupScore: categoryGroupConceptScore(column),
      bucketScore: categoryBucketConceptScore(column),
    }))
    .filter((candidate) => candidate.distinctBucketCount > 0);
  const group = candidates
    .filter((candidate) => candidate.groupScore > 0)
    .sort(
      (left, right) =>
        right.groupScore - left.groupScore ||
        right.distinctBucketCount - left.distinctBucketCount ||
        left.index - right.index,
    )[0];
  if (!group) {
    return null;
  }
  const bucket = candidates
    .filter(
      (candidate) =>
        candidate.column.fieldId !== group.column.fieldId &&
        candidate.distinctBucketCount >= 2,
    )
    .sort(
      (left, right) =>
        right.bucketScore - left.bucketScore ||
        right.distinctBucketCount - left.distinctBucketCount ||
        left.index - right.index,
    )[0];
  return bucket ? { bucket: bucket.column, group: group.column } : null;
};

const chooseMetricAggregate = (
  column: ReportGenerationAdvancedVisualizationChartColumn | null,
): "sum" | "average" => {
  const text = `${column?.fieldId ?? ""} ${column?.label ?? ""}`;
  if (
    /\b(avg|average|ratio|rate|percent|percentage|balance|pct)\b|%/i.test(text)
  ) {
    return "average";
  }
  return "sum";
};

type NumericConceptBucketKind = "money" | "percent" | "days" | "count";

type NumericConceptBucket = {
  key: string;
  label: string;
  order: number;
};

const resolveNumericConceptBucketKind = (
  column: ReportGenerationAdvancedVisualizationChartColumn,
): NumericConceptBucketKind | null => {
  if (column.fieldType !== "number") {
    return null;
  }
  const text = `${column.fieldId} ${column.label}`;
  if (/\b(days?|pending|age)\b/i.test(text)) {
    return "days";
  }
  if (/\b(pct|percent|percentage|ratio|rate)\b|%/i.test(text)) {
    return "percent";
  }
  if (
    /\b(income|tuition|balance|amount|award|paid|due|charged|fee|debit|credit|contract|expense)\b/i.test(
      text,
    )
  ) {
    return "money";
  }
  if (/\b(count|missing|flags?|students?)\b/i.test(text)) {
    return "count";
  }
  return null;
};

const readNumericConceptBucket = (
  value: unknown,
  kind: NumericConceptBucketKind,
): NumericConceptBucket | null => {
  const numericValue = readNumericValue(value);
  if (numericValue === null) {
    return null;
  }

  if (kind === "days") {
    if (numericValue <= 7)
      return { key: "days-0-7", label: "0-7 days", order: 10 };
    if (numericValue <= 14)
      return { key: "days-8-14", label: "8-14 days", order: 20 };
    if (numericValue <= 30)
      return { key: "days-15-30", label: "15-30 days", order: 30 };
    if (numericValue <= 60)
      return { key: "days-31-60", label: "31-60 days", order: 40 };
    return { key: "days-61-plus", label: "61+ days", order: 50 };
  }

  if (kind === "percent") {
    const normalized =
      Math.abs(numericValue) <= 1 ? numericValue * 100 : numericValue;
    if (normalized < 20)
      return { key: "pct-under-20", label: "Under 20%", order: 10 };
    if (normalized < 40)
      return { key: "pct-20-40", label: "20-40%", order: 20 };
    if (normalized < 60)
      return { key: "pct-40-60", label: "40-60%", order: 30 };
    if (normalized < 80)
      return { key: "pct-60-80", label: "60-80%", order: 40 };
    return { key: "pct-80-plus", label: "80%+", order: 50 };
  }

  if (kind === "count") {
    if (numericValue <= 0) return { key: "count-zero", label: "0", order: 10 };
    if (numericValue <= 1) return { key: "count-one", label: "1", order: 20 };
    if (numericValue <= 3)
      return { key: "count-two-three", label: "2-3", order: 30 };
    return { key: "count-four-plus", label: "4+", order: 40 };
  }

  if (numericValue < 50_000)
    return { key: "money-under-50k", label: "Under $50k", order: 10 };
  if (numericValue < 100_000)
    return { key: "money-50k-100k", label: "$50k-$100k", order: 20 };
  if (numericValue < 150_000)
    return { key: "money-100k-150k", label: "$100k-$150k", order: 30 };
  if (numericValue < 250_000)
    return { key: "money-150k-250k", label: "$150k-$250k", order: 40 };
  return { key: "money-250k-plus", label: "$250k+", order: 50 };
};

const numericConceptDimensionScore = (
  column: ReportGenerationAdvancedVisualizationChartColumn,
): number => {
  const text = `${column.fieldId} ${column.label}`;
  const kind = resolveNumericConceptBucketKind(column);
  if (!kind) {
    return -1;
  }
  let score = 20;
  if (/\breported_income\b/i.test(text)) score += 100;
  if (/\bincome_per_child\b/i.test(text)) score += 80;
  if (
    /\bincome\b/i.test(text) &&
    !/\b(expense|ratio|rate|pct|percent|percentage)\b/i.test(text)
  )
    score += 60;
  if (/\b(days?|pending|age)\b/i.test(text)) score += 50;
  if (/\b(expense.*ratio|ratio|rate|pct|percent|percentage)\b|%/i.test(text))
    score += 35;
  if (/\b(tuition|balance|amount|award|due|paid|contract)\b/i.test(text))
    score += 25;
  if (
    /\b(system|recommendation|override|award_pct|award percentage)\b/i.test(
      text,
    )
  )
    score -= 20;
  return score;
};

const numericConceptMetricScore = (
  column: ReportGenerationAdvancedVisualizationChartColumn,
): number => {
  const text = `${column.fieldId} ${column.label}`;
  let score = 10;
  if (/\b(system_rec_pct|system|recommendation|rec)\b/i.test(text)) score += 90;
  if (/\b(award_pct|award percentage)\b|%/i.test(text)) score += 55;
  if (
    /\b(override|award|amount|balance|tuition|due|paid|contract)\b/i.test(text)
  )
    score += 35;
  if (/\b(ratio|rate|pct|percent|percentage)\b/i.test(text)) score += 25;
  if (/\b(reported_income|income_per_child)\b/i.test(text)) score -= 35;
  return score;
};

const columnHasNumericData = (
  rows: readonly ReportGenerationVisualizationPolicyEngineRow[],
  column: ReportGenerationAdvancedVisualizationChartColumn,
): boolean =>
  rows.some((row) => readNumericValue(row.cells[column.fieldId]) !== null);

const chooseNumericConceptDimension = (input: {
  rows: readonly ReportGenerationVisualizationPolicyEngineRow[];
  numericColumns: readonly ReportGenerationAdvancedVisualizationChartColumn[];
}): {
  column: ReportGenerationAdvancedVisualizationChartColumn;
  kind: NumericConceptBucketKind;
} | null => {
  const candidates = input.numericColumns
    .map((column, index) => ({
      column,
      index,
      kind: resolveNumericConceptBucketKind(column),
      score: numericConceptDimensionScore(column),
    }))
    .filter(
      (
        candidate,
      ): candidate is {
        column: ReportGenerationAdvancedVisualizationChartColumn;
        index: number;
        kind: NumericConceptBucketKind;
        score: number;
      } =>
        Boolean(candidate.kind) &&
        candidate.score >= 0 &&
        columnHasNumericData(input.rows, candidate.column),
    )
    .sort(
      (left, right) => right.score - left.score || left.index - right.index,
    );

  const selected = candidates[0];
  return selected ? { column: selected.column, kind: selected.kind } : null;
};

const chooseNumericConceptMetric = (input: {
  rows: readonly ReportGenerationVisualizationPolicyEngineRow[];
  numericColumns: readonly ReportGenerationAdvancedVisualizationChartColumn[];
  dimension: ReportGenerationAdvancedVisualizationChartColumn;
}): ReportGenerationAdvancedVisualizationChartColumn | null =>
  input.numericColumns
    .filter(
      (column) =>
        column.fieldId !== input.dimension.fieldId &&
        columnHasNumericData(input.rows, column),
    )
    .map((column, index) => ({
      column,
      index,
      score: numericConceptMetricScore(column),
    }))
    .sort(
      (left, right) => right.score - left.score || left.index - right.index,
    )[0]?.column ?? null;

type BucketAggregate = {
  key: string;
  label: string;
  count: number;
  sum: number;
  metricCount: number;
  xSum: number;
  xCount: number;
  ySum: number;
  yCount: number;
};

type NumericConceptBucketAggregate = BucketAggregate & { order: number };

const createBucket = (key: string, label: string): BucketAggregate => ({
  key,
  label,
  count: 0,
  sum: 0,
  metricCount: 0,
  xSum: 0,
  xCount: 0,
  ySum: 0,
  yCount: 0,
});

const formatCompactNumber = (value: number): string => {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    return `${Number((value / 1_000_000).toFixed(1))}M`;
  }
  if (abs >= 1_000) {
    return `${Math.round(value / 1_000)}k`;
  }
  if (abs >= 10) {
    return String(Math.round(value));
  }
  return String(Number(value.toFixed(1)));
};

const formatAdaptiveBucketBound = (
  value: number,
  kind: NumericConceptBucketKind,
): string => {
  if (kind === "money") {
    return `$${formatCompactNumber(value)}`;
  }
  if (kind === "percent") {
    return `${formatCompactNumber(value)}%`;
  }
  if (kind === "days") {
    return `${formatCompactNumber(value)} days`;
  }
  return formatCompactNumber(value);
};

const normalizeAdaptiveDimensionValue = (
  value: number,
  kind: NumericConceptBucketKind,
): number => {
  if (kind === "percent" && Math.abs(value) <= 1) {
    return value * 100;
  }
  return value;
};

const formatAdaptiveBucketLabel = (
  min: number,
  max: number,
  kind: NumericConceptBucketKind,
): string => {
  if (Math.abs(max - min) < Number.EPSILON) {
    return formatAdaptiveBucketBound(min, kind);
  }
  return `${formatAdaptiveBucketBound(min, kind)}-${formatAdaptiveBucketBound(max, kind)}`;
};

const toChartPoint = (
  bucket: NumericConceptBucketAggregate,
  input: {
    metric: ReportGenerationAdvancedVisualizationChartColumn | null;
    aggregate: "sum" | "average" | "count";
    chartType: ReportGenerationAdvancedVisualizationChartType;
    optionId: string;
  },
): ReportGenerationAdvancedVisualizationChartPoint | null => {
  const average =
    bucket.metricCount > 0 ? bucket.sum / bucket.metricCount : null;
  const value =
    input.aggregate === "count"
      ? bucket.count
      : input.aggregate === "average"
        ? average
        : bucket.sum;
  if (value === null) {
    return null;
  }
  return {
    optionId: input.optionId,
    chartType: input.chartType,
    seriesId: input.metric?.fieldId ?? "rows",
    seriesLabel: input.metric?.label ?? "Rows",
    seriesColor: CHART_SERIES_COLORS[0],
    key: bucket.key,
    label: bucket.label,
    bucket: bucket.label,
    count: bucket.count,
    value,
    sum: bucket.metricCount > 0 ? bucket.sum : null,
    average,
  };
};

const buildDimensionBuckets = (input: {
  rows: readonly ReportGenerationVisualizationPolicyEngineRow[];
  dimension: ReportGenerationAdvancedVisualizationChartColumn | null;
  metric: ReportGenerationAdvancedVisualizationChartColumn | null;
  xMetric?: ReportGenerationAdvancedVisualizationChartColumn | null;
  yMetric?: ReportGenerationAdvancedVisualizationChartColumn | null;
  aggregate: "sum" | "average" | "count";
  chartType: ReportGenerationAdvancedVisualizationChartType;
  optionId: string;
}): ReportGenerationAdvancedVisualizationChartPoint[] => {
  const buckets = new Map<string, BucketAggregate>();
  for (const row of input.rows) {
    const rawDimensionValue = input.dimension
      ? row.cells[input.dimension.fieldId]
      : null;
    const label =
      input.dimension?.fieldType === "date"
        ? readIsoMonthBucket(rawDimensionValue)
        : input.dimension
          ? readCategoryBucket(rawDimensionValue, input.dimension)
          : "All rows";
    if (!label) {
      continue;
    }
    const key = input.dimension
      ? `${input.dimension.fieldId}:${normalizeKeyPart(label)}`
      : "all-rows";
    const bucket = buckets.get(key) ?? createBucket(key, label);
    bucket.count += 1;
    const metricValue = input.metric
      ? readNumericValue(row.cells[input.metric.fieldId])
      : null;
    if (metricValue !== null) {
      bucket.sum += metricValue;
      bucket.metricCount += 1;
    }
    const xValue = input.xMetric
      ? readNumericValue(row.cells[input.xMetric.fieldId])
      : null;
    if (xValue !== null) {
      bucket.xSum += xValue;
      bucket.xCount += 1;
    }
    const yValue = input.yMetric
      ? readNumericValue(row.cells[input.yMetric.fieldId])
      : null;
    if (yValue !== null) {
      bucket.ySum += yValue;
      bucket.yCount += 1;
    }
    buckets.set(key, bucket);
  }
  return [...buckets.values()]
    .sort((left, right) => left.label.localeCompare(right.label))
    .map((bucket) => {
      const average =
        bucket.metricCount > 0 ? bucket.sum / bucket.metricCount : null;
      const value =
        input.aggregate === "count"
          ? bucket.count
          : input.aggregate === "average"
            ? average
            : bucket.sum;
      return {
        optionId: input.optionId,
        chartType: input.chartType,
        seriesId: input.metric?.fieldId ?? input.optionId,
        seriesLabel: input.metric?.label ?? "Rows",
        seriesColor: CHART_SERIES_COLORS[0],
        key: bucket.key,
        label: bucket.label,
        bucket: bucket.label,
        count: bucket.count,
        value,
        sum: bucket.metricCount > 0 ? bucket.sum : null,
        average,
        x: bucket.xCount > 0 ? bucket.xSum / bucket.xCount : null,
        y: bucket.yCount > 0 ? bucket.ySum / bucket.yCount : null,
      };
    })
    .filter((point) =>
      input.chartType === "scatter"
        ? point.x !== null && point.y !== null
        : point.value !== null,
    );
};

const buildRowLevelScatterPoints = (input: {
  rows: readonly ReportGenerationVisualizationPolicyEngineRow[];
  xMetric: ReportGenerationAdvancedVisualizationChartColumn;
  yMetric: ReportGenerationAdvancedVisualizationChartColumn;
  group: ReportGenerationAdvancedVisualizationChartColumn | null;
  optionId: string;
}): ReportGenerationAdvancedVisualizationChartPoint[] => {
  const seriesByLabel = new Map<string, number>();
  return input.rows.flatMap((row, index) => {
    const x = readNumericValue(row.cells[input.xMetric.fieldId]);
    const y = readNumericValue(row.cells[input.yMetric.fieldId]);
    if (x === null || y === null) {
      return [];
    }
    const groupLabel = input.group
      ? input.group.fieldType === "date"
        ? readIsoMonthBucket(row.cells[input.group.fieldId])
        : readCategoryBucket(row.cells[input.group.fieldId], input.group)
      : null;
    const seriesLabel = groupLabel || "Rows";
    const seriesId = normalizeKeyPart(seriesLabel);
    const existingSeriesIndex = seriesByLabel.get(seriesId);
    const seriesIndex = existingSeriesIndex ?? seriesByLabel.size;
    if (existingSeriesIndex === undefined) {
      seriesByLabel.set(seriesId, seriesIndex);
    }
    return [
      {
        optionId: input.optionId,
        chartType: "scatter" as const,
        seriesId,
        seriesLabel,
        seriesColor:
          CHART_SERIES_COLORS[seriesIndex % CHART_SERIES_COLORS.length],
        key: `row-${index + 1}`,
        label: groupLabel
          ? `${groupLabel} row ${index + 1}`
          : `Row ${index + 1}`,
        bucket: groupLabel ?? null,
        count: 1,
        value: 1,
        sum: null,
        average: null,
        x,
        y,
      },
    ];
  });
};

const buildStaticNumericConceptBucketPoints = (input: {
  rows: readonly ReportGenerationVisualizationPolicyEngineRow[];
  dimension: ReportGenerationAdvancedVisualizationChartColumn;
  dimensionKind: NumericConceptBucketKind;
  metric: ReportGenerationAdvancedVisualizationChartColumn | null;
  aggregate: "sum" | "average" | "count";
  chartType: ReportGenerationAdvancedVisualizationChartType;
  optionId: string;
}): ReportGenerationAdvancedVisualizationChartPoint[] => {
  const buckets = new Map<string, NumericConceptBucketAggregate>();
  for (const row of input.rows) {
    const conceptBucket = readNumericConceptBucket(
      row.cells[input.dimension.fieldId],
      input.dimensionKind,
    );
    if (!conceptBucket) {
      continue;
    }
    const metricValue = input.metric
      ? readNumericValue(row.cells[input.metric.fieldId])
      : null;
    if (input.metric && metricValue === null) {
      continue;
    }
    const key = `${input.dimension.fieldId}:${conceptBucket.key}`;
    const bucket = buckets.get(key) ?? {
      ...createBucket(key, conceptBucket.label),
      order: conceptBucket.order,
    };
    bucket.count += 1;
    if (metricValue !== null) {
      bucket.sum += metricValue;
      bucket.metricCount += 1;
    }
    buckets.set(key, bucket);
  }

  return [...buckets.values()]
    .sort(
      (left, right) =>
        left.order - right.order || left.label.localeCompare(right.label),
    )
    .map((bucket) => toChartPoint(bucket, input))
    .filter(
      (point): point is ReportGenerationAdvancedVisualizationChartPoint =>
        point !== null,
    );
};

const buildAdaptiveNumericConceptBucketPoints = (input: {
  rows: readonly ReportGenerationVisualizationPolicyEngineRow[];
  dimension: ReportGenerationAdvancedVisualizationChartColumn;
  dimensionKind: NumericConceptBucketKind;
  metric: ReportGenerationAdvancedVisualizationChartColumn | null;
  aggregate: "sum" | "average" | "count";
  chartType: ReportGenerationAdvancedVisualizationChartType;
  optionId: string;
}): ReportGenerationAdvancedVisualizationChartPoint[] => {
  const entries = input.rows.flatMap((row) => {
    const rawDimensionValue = readNumericValue(
      row.cells[input.dimension.fieldId],
    );
    if (rawDimensionValue === null) {
      return [];
    }
    const metricValue = input.metric
      ? readNumericValue(row.cells[input.metric.fieldId])
      : null;
    if (input.metric && metricValue === null) {
      return [];
    }
    return [
      {
        dimensionValue: normalizeAdaptiveDimensionValue(
          rawDimensionValue,
          input.dimensionKind,
        ),
        metricValue,
      },
    ];
  });
  const distinctValues = [
    ...new Set(entries.map((entry) => Number(entry.dimensionValue.toFixed(6)))),
  ].sort((left, right) => left - right);
  if (distinctValues.length < 2) {
    return [];
  }

  const min = distinctValues[0];
  const max = distinctValues[distinctValues.length - 1];
  if (min === undefined || max === undefined || max <= min) {
    return [];
  }

  const bucketCount = Math.min(4, distinctValues.length);
  const span = max - min;
  const buckets = new Map<number, NumericConceptBucketAggregate>();
  for (const entry of entries) {
    const ratio = (entry.dimensionValue - min) / span;
    const bucketIndex = Math.min(
      bucketCount - 1,
      Math.max(0, Math.floor(ratio * bucketCount)),
    );
    const bucketMin = min + (span * bucketIndex) / bucketCount;
    const bucketMax =
      bucketIndex === bucketCount - 1
        ? max
        : min + (span * (bucketIndex + 1)) / bucketCount;
    const label = formatAdaptiveBucketLabel(
      bucketMin,
      bucketMax,
      input.dimensionKind,
    );
    const bucket = buckets.get(bucketIndex) ?? {
      ...createBucket(
        `${input.dimension.fieldId}:adaptive-${bucketIndex}`,
        label,
      ),
      order: bucketIndex,
    };
    bucket.count += 1;
    if (entry.metricValue !== null) {
      bucket.sum += entry.metricValue;
      bucket.metricCount += 1;
    }
    buckets.set(bucketIndex, bucket);
  }

  return [...buckets.values()]
    .sort((left, right) => left.order - right.order)
    .map((bucket) => toChartPoint(bucket, input))
    .filter(
      (point): point is ReportGenerationAdvancedVisualizationChartPoint =>
        point !== null,
    );
};

const buildNumericConceptBucketPoints = (input: {
  rows: readonly ReportGenerationVisualizationPolicyEngineRow[];
  dimension: ReportGenerationAdvancedVisualizationChartColumn;
  dimensionKind: NumericConceptBucketKind;
  metric: ReportGenerationAdvancedVisualizationChartColumn | null;
  aggregate: "sum" | "average" | "count";
  chartType: ReportGenerationAdvancedVisualizationChartType;
  optionId: string;
}): ReportGenerationAdvancedVisualizationChartPoint[] => {
  const staticPoints = buildStaticNumericConceptBucketPoints(input);
  if (staticPoints.length >= 2) {
    return staticPoints;
  }
  const adaptivePoints = buildAdaptiveNumericConceptBucketPoints(input);
  return adaptivePoints.length >= 2 ? adaptivePoints : staticPoints;
};

const buildMetricComparisonBuckets = (input: {
  rows: readonly ReportGenerationVisualizationPolicyEngineRow[];
  dimension: ReportGenerationAdvancedVisualizationChartColumn;
  metrics: readonly ReportGenerationAdvancedVisualizationChartColumn[];
  chartType: ReportGenerationAdvancedVisualizationChartType;
  optionId: string;
}): ReportGenerationAdvancedVisualizationChartPoint[] =>
  input.metrics.flatMap((metric, seriesIndex) =>
    buildDimensionBuckets({
      rows: input.rows,
      dimension: input.dimension,
      metric,
      aggregate: chooseMetricAggregate(metric),
      chartType: input.chartType,
      optionId: input.optionId,
    }).map((point) => ({
      ...point,
      seriesId: metric.fieldId,
      seriesLabel: metric.label,
      seriesColor:
        CHART_SERIES_COLORS[seriesIndex % CHART_SERIES_COLORS.length],
    })),
  );

const buildGroupedMetricBuckets = (input: {
  rows: readonly ReportGenerationVisualizationPolicyEngineRow[];
  dimension: ReportGenerationAdvancedVisualizationChartColumn;
  group: ReportGenerationAdvancedVisualizationChartColumn;
  metric: ReportGenerationAdvancedVisualizationChartColumn;
  aggregate: "sum" | "average" | "count";
  chartType: ReportGenerationAdvancedVisualizationChartType;
  optionId: string;
}): ReportGenerationAdvancedVisualizationChartPoint[] => {
  const buckets = new Map<string, BucketAggregate>();
  const bucketLabels = new Set<string>();
  const groupLabels = new Set<string>();
  for (const row of input.rows) {
    const rawDimensionValue = row.cells[input.dimension.fieldId];
    const bucketLabel =
      input.dimension.fieldType === "date"
        ? readIsoMonthBucket(rawDimensionValue)
        : readCategoryBucket(rawDimensionValue, input.dimension);
    const groupLabel = readCategoryBucket(
      row.cells[input.group.fieldId],
      input.group,
    );
    if (!bucketLabel || !groupLabel) {
      continue;
    }
    const metricValue = readNumericValue(row.cells[input.metric.fieldId]);
    if (metricValue === null) {
      continue;
    }
    bucketLabels.add(bucketLabel);
    groupLabels.add(groupLabel);
    const key = `${normalizeKeyPart(groupLabel)}:${normalizeKeyPart(bucketLabel)}`;
    const bucket = buckets.get(key) ?? createBucket(key, bucketLabel);
    bucket.count += 1;
    bucket.sum += metricValue;
    bucket.metricCount += 1;
    buckets.set(key, bucket);
  }

  const sortedBucketLabels = [...bucketLabels].sort();
  return [...groupLabels]
    .sort()
    .flatMap((groupLabel, seriesIndex) =>
      sortedBucketLabels.map((bucketLabel) => {
        const key = `${normalizeKeyPart(groupLabel)}:${normalizeKeyPart(bucketLabel)}`;
        const bucket = buckets.get(key);
        const value = !bucket
          ? 0
          : input.aggregate === "average"
            ? bucket.sum / Math.max(bucket.metricCount, 1)
            : input.aggregate === "count"
              ? bucket.count
              : bucket.sum;
        return {
          optionId: input.optionId,
          chartType: input.chartType,
          seriesId: normalizeKeyPart(groupLabel),
          seriesLabel: groupLabel,
          seriesColor:
            CHART_SERIES_COLORS[seriesIndex % CHART_SERIES_COLORS.length],
          key: `${input.dimension.fieldId}:${normalizeKeyPart(bucketLabel)}`,
          label: bucketLabel,
          bucket: bucketLabel,
          count: bucket?.count ?? 0,
          value,
          sum: bucket?.sum ?? 0,
          average:
            bucket && bucket.metricCount > 0
              ? bucket.sum / bucket.metricCount
              : 0,
        };
      }),
    )
    .filter((point) => point.value !== null);
};

const buildGroupedCountBuckets = (input: {
  rows: readonly ReportGenerationVisualizationPolicyEngineRow[];
  dimension: ReportGenerationAdvancedVisualizationChartColumn;
  group: ReportGenerationAdvancedVisualizationChartColumn;
  chartType: ReportGenerationAdvancedVisualizationChartType;
  optionId: string;
}): ReportGenerationAdvancedVisualizationChartPoint[] => {
  const buckets = new Map<string, BucketAggregate>();
  const bucketLabels = new Set<string>();
  const groupLabels = new Set<string>();
  for (const row of input.rows) {
    const bucketLabel = readCategoryBucket(
      row.cells[input.dimension.fieldId],
      input.dimension,
    );
    const groupLabel = readCategoryBucket(
      row.cells[input.group.fieldId],
      input.group,
    );
    if (!bucketLabel || !groupLabel) {
      continue;
    }
    bucketLabels.add(bucketLabel);
    groupLabels.add(groupLabel);
    const key = `${normalizeKeyPart(groupLabel)}:${normalizeKeyPart(bucketLabel)}`;
    const bucket = buckets.get(key) ?? createBucket(key, bucketLabel);
    bucket.count += 1;
    buckets.set(key, bucket);
  }

  const sortedBucketLabels = [...bucketLabels].sort();
  return [...groupLabels].sort().flatMap((groupLabel, seriesIndex) =>
    sortedBucketLabels.map((bucketLabel) => {
      const key = `${normalizeKeyPart(groupLabel)}:${normalizeKeyPart(bucketLabel)}`;
      const bucket = buckets.get(key);
      const value = bucket?.count ?? 0;
      return {
        optionId: input.optionId,
        chartType: input.chartType,
        seriesId: normalizeKeyPart(groupLabel),
        seriesLabel: groupLabel,
        seriesColor:
          CHART_SERIES_COLORS[seriesIndex % CHART_SERIES_COLORS.length],
        key: `${input.dimension.fieldId}:${normalizeKeyPart(bucketLabel)}`,
        label: bucketLabel,
        bucket: bucketLabel,
        count: value,
        value,
        sum: null,
        average: null,
      };
    }),
  );
};

const MONTH_SHORT_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

const readCalendarMonthBucket = (
  value: unknown,
): { year: number; monthIndex: number; label: string } | null => {
  const text = String(value ?? "").trim();
  if (!text) {
    return null;
  }
  const isoMatch = text.match(/^(\d{4})-(\d{2})/);
  if (isoMatch) {
    const year = Number(isoMatch[1]);
    const monthIndex = Number(isoMatch[2]) - 1;
    if (Number.isInteger(year) && monthIndex >= 0 && monthIndex < 12) {
      return {
        year,
        monthIndex,
        label: MONTH_SHORT_LABELS[monthIndex],
      };
    }
  }
  const time = Date.parse(text);
  if (!Number.isFinite(time)) {
    return null;
  }
  const date = new Date(time);
  const monthIndex = date.getUTCMonth();
  return {
    year: date.getUTCFullYear(),
    monthIndex,
    label: MONTH_SHORT_LABELS[monthIndex],
  };
};

const readAcademicYearBucketYear = (value: unknown): number | null => {
  const text = String(value ?? "").trim();
  if (!text) {
    return null;
  }

  const rangeMatch = text.match(/(\d{4})\D+(\d{2,4})/);
  if (rangeMatch) {
    const startYear = Number(rangeMatch[1]);
    const endYearRaw = rangeMatch[2] ?? "";
    let endYear = Number(endYearRaw);
    if (endYearRaw.length === 2 && Number.isInteger(startYear)) {
      endYear = Math.floor(startYear / 100) * 100 + endYear;
      if (endYear < startYear) {
        endYear += 100;
      }
    }
    if (Number.isInteger(endYear)) {
      return endYear;
    }
  }

  const yearMatches = text.match(/\b\d{4}\b/g);
  const lastYear = yearMatches?.[yearMatches.length - 1];
  if (!lastYear) {
    return null;
  }
  const year = Number(lastYear);
  return Number.isInteger(year) ? year : null;
};

const buildTuitionPriorYearOverlayBuckets = (input: {
  rows: readonly ReportGenerationVisualizationPolicyEngineRow[];
  columns: readonly ReportGenerationAdvancedVisualizationChartColumn[];
  primaryDate: ReportGenerationAdvancedVisualizationChartColumn | null;
}): {
  currentYear: number;
  priorYear: number;
  metric: ReportGenerationAdvancedVisualizationChartColumn;
  barPoints: ReportGenerationAdvancedVisualizationChartPoint[];
  linePoints: ReportGenerationAdvancedVisualizationChartPoint[];
} | null => {
  const academicYearColumn = input.columns.find(
    (column) => column.fieldId === "academic_year",
  );
  const dueDateColumn =
    input.columns.find((column) => column.fieldId === "due_date") ??
    input.primaryDate;
  const metric =
    input.columns.find((column) => column.fieldId === "balance_remaining") ??
    input.columns.find((column) => column.fieldId === "amount_due") ??
    null;
  if (!academicYearColumn || !dueDateColumn || !metric) {
    return null;
  }

  const parsedRows = input.rows.flatMap((row) => {
    const month = readCalendarMonthBucket(row.cells[dueDateColumn.fieldId]);
    const rowYear =
      readAcademicYearBucketYear(row.cells[academicYearColumn.fieldId]) ??
      month?.year ??
      null;
    const value = readNumericValue(row.cells[metric.fieldId]);
    if (!month || rowYear === null || value === null || value <= 0) {
      return [];
    }
    return [{ ...month, year: rowYear, value }];
  });
  const years = sortedUnique(parsedRows.map((row) => row.year))
    .map(Number)
    .filter((year) => Number.isInteger(year))
    .sort((left, right) => left - right);
  const currentYear = years[years.length - 1];
  if (currentYear === undefined || !Number.isInteger(currentYear)) {
    return null;
  }
  const priorYear = currentYear - 1;
  if (!years.includes(priorYear)) {
    return null;
  }

  const monthIndexes = [
    ...new Set(
      parsedRows
        .filter((row) => row.year === currentYear || row.year === priorYear)
        .map((row) => row.monthIndex),
    ),
  ].sort((left, right) => left - right);
  if (monthIndexes.length === 0) {
    return null;
  }

  const totals = new Map<string, { value: number; count: number }>();
  for (const row of parsedRows) {
    if (row.year !== currentYear && row.year !== priorYear) {
      continue;
    }
    const key = `${row.year}:${row.monthIndex}`;
    const total = totals.get(key) ?? { value: 0, count: 0 };
    total.value += row.value;
    total.count += 1;
    totals.set(key, total);
  }

  const toPoints = (
    optionId: string,
    chartType: ReportGenerationAdvancedVisualizationChartType,
  ) =>
    [
      {
        year: currentYear,
        seriesId: "current-year",
        seriesLabel: `${currentYear} outstanding`,
        color: CHART_SERIES_COLORS[0],
      },
      {
        year: priorYear,
        seriesId: "prior-year",
        seriesLabel: `${priorYear} outstanding`,
        color: CHART_SERIES_COLORS[1],
      },
    ].flatMap((series) =>
      monthIndexes.map((monthIndex) => {
        const total = totals.get(`${series.year}:${monthIndex}`) ?? {
          value: 0,
          count: 0,
        };
        return {
          optionId,
          chartType,
          seriesId: series.seriesId,
          seriesLabel: series.seriesLabel,
          seriesColor: series.color,
          key: `due-month:${String(monthIndex + 1).padStart(2, "0")}`,
          label: MONTH_SHORT_LABELS[monthIndex],
          bucket: MONTH_SHORT_LABELS[monthIndex],
          count: total.count,
          value: total.value,
          sum: total.value,
          average: total.count > 0 ? total.value / total.count : 0,
        };
      }),
    );

  return {
    currentYear,
    priorYear,
    metric,
    barPoints: toPoints("chart:bar:tuition-outstanding-prior-year", "bar"),
    linePoints: toPoints("chart:line:tuition-outstanding-prior-year", "line"),
  };
};

const capPiePoints = (
  points: readonly ReportGenerationAdvancedVisualizationChartPoint[],
  optionId: string,
): ReportGenerationAdvancedVisualizationChartPoint[] => {
  const sorted = [...points].sort(
    (left, right) =>
      Number(right.value ?? 0) - Number(left.value ?? 0) ||
      left.label.localeCompare(right.label),
  );
  const head = sorted.slice(0, 8);
  const tail = sorted.slice(8);
  if (tail.length === 0) {
    return head;
  }
  const otherValue = tail.reduce(
    (sum, point) => sum + Number(point.value ?? 0),
    0,
  );
  const otherCount = tail.reduce((sum, point) => sum + point.count, 0);
  return [
    ...head,
    {
      optionId,
      chartType: "pie",
      key: "other",
      label: "Other",
      bucket: "Other",
      count: otherCount,
      value: otherValue,
      sum: otherValue,
      average: otherCount > 0 ? otherValue / otherCount : null,
    },
  ];
};

const columnByPreferredFieldId = (
  columns: readonly ReportGenerationAdvancedVisualizationChartColumn[],
  fieldIds: readonly string[],
): ReportGenerationAdvancedVisualizationChartColumn | null =>
  fieldIds
    .map((fieldId) => columns.find((column) => column.fieldId === fieldId))
    .find(
      (column): column is ReportGenerationAdvancedVisualizationChartColumn =>
        Boolean(column),
    ) ?? null;

const allColumnsByPreferredFieldIds = (
  columns: readonly ReportGenerationAdvancedVisualizationChartColumn[],
  fieldIds: readonly string[],
): ReportGenerationAdvancedVisualizationChartColumn[] =>
  fieldIds.flatMap((fieldId) => {
    const column = columns.find((candidate) => candidate.fieldId === fieldId);
    return column ? [column] : [];
  });

const mappedRenderKindForChartType = (
  chartType: ReportGenerationAdvancedVisualizationChartType,
): "grouped-bar" | "ribbon-line" | null => {
  if (chartType === "bar") {
    return "grouped-bar";
  }
  if (chartType === "line") {
    return "ribbon-line";
  }
  return null;
};

const buildTemplateMappedChartOptionBase = (input: {
  mapping: RunReportsTemplateVisualizationMapping;
  chartType: ReportGenerationAdvancedVisualizationChartType;
  idSuffix: string;
  metric: ReportGenerationAdvancedVisualizationChartColumn | null;
  dimension: ReportGenerationAdvancedVisualizationChartColumn | null;
  group: ReportGenerationAdvancedVisualizationChartColumn | null;
  aggregate: "sum" | "average" | "count";
}): Omit<ReportGenerationAdvancedVisualizationChartOption, "pointCount"> => ({
  id: `chart:${input.chartType}:${input.mapping.useCaseId}:${input.idSuffix}`,
  chartType: input.chartType,
  renderKind: mappedRenderKindForChartType(input.chartType),
  useCaseId: input.mapping.useCaseId,
  templateId: input.mapping.templateId,
  templateMappingId: input.mapping.id,
  label: input.metric
    ? `${input.metric.label} by ${input.dimension?.label ?? input.group?.label ?? "report cohort"}`
    : `${input.mapping.label}`,
  aggregate: input.aggregate,
  metricFieldId: input.metric?.fieldId ?? null,
  metricLabel:
    input.metric?.label ?? (input.aggregate === "count" ? "Rows" : null),
  dimensionFieldId: input.dimension?.fieldId ?? null,
  dimensionLabel: input.dimension?.label ?? null,
  groupFieldId: input.group?.fieldId ?? null,
  groupLabel: input.group?.label ?? null,
  animationVariant: input.chartType === "line" ? "smooth" : "staggered",
  animationLabel:
    input.chartType === "line" ? "Smooth reveal" : "Staggered reveal",
});

const buildTemplateMappedOptions = (input: {
  mapping: RunReportsTemplateVisualizationMapping;
  rows: readonly ReportGenerationVisualizationPolicyEngineRow[];
  columns: readonly ReportGenerationAdvancedVisualizationChartColumn[];
}): Array<{
  option: Omit<ReportGenerationAdvancedVisualizationChartOption, "pointCount">;
  points: ReportGenerationAdvancedVisualizationChartPoint[];
}> => {
  if (input.mapping.primarySurface === "table") {
    return [];
  }

  const dimension = columnByPreferredFieldId(
    input.columns,
    input.mapping.dimensionFieldIds,
  );
  const group = columnByPreferredFieldId(
    input.columns,
    input.mapping.groupFieldIds,
  );
  const metrics = allColumnsByPreferredFieldIds(
    input.columns,
    input.mapping.metricFieldIds,
  );
  const primaryMetric = metrics[0] ?? null;
  const aggregate = input.mapping.aggregate;
  const options: Array<{
    option: Omit<
      ReportGenerationAdvancedVisualizationChartOption,
      "pointCount"
    >;
    points: ReportGenerationAdvancedVisualizationChartPoint[];
  }> = [];

  for (const chartType of input.mapping.chartTypes) {
    if (chartType === "scatter") {
      const xMetric = input.mapping.xFieldId
        ? input.columns.find(
            (column) => column.fieldId === input.mapping.xFieldId,
          )
        : metrics[0];
      const yMetric = input.mapping.yFieldId
        ? input.columns.find(
            (column) => column.fieldId === input.mapping.yFieldId,
          )
        : metrics.find((metric) => metric.fieldId !== xMetric?.fieldId);
      if (!xMetric || !yMetric) {
        continue;
      }
      const optionId = `chart:scatter:${input.mapping.useCaseId}:mapped`;
      options.push({
        option: {
          id: optionId,
          chartType: "scatter",
          useCaseId: input.mapping.useCaseId,
          templateId: input.mapping.templateId,
          templateMappingId: input.mapping.id,
          label: `${xMetric.label} vs ${yMetric.label}`,
          aggregate: "average",
          xFieldId: xMetric.fieldId,
          xLabel: xMetric.label,
          yFieldId: yMetric.fieldId,
          yLabel: yMetric.label,
          dimensionFieldId: dimension?.fieldId ?? group?.fieldId ?? null,
          dimensionLabel: dimension?.label ?? group?.label ?? null,
        },
        points: buildRowLevelScatterPoints({
          rows: input.rows,
          xMetric,
          yMetric,
          group: group ?? dimension,
          optionId,
        }),
      });
      continue;
    }

    if (chartType === "pie") {
      const pieDimension = group ?? dimension;
      if (!pieDimension) {
        continue;
      }
      const optionId = `chart:pie:${input.mapping.useCaseId}:mapped`;
      const points = capPiePoints(
        buildDimensionBuckets({
          rows: input.rows,
          dimension: pieDimension,
          metric: primaryMetric,
          aggregate: primaryMetric ? aggregate : "count",
          chartType: "pie",
          optionId,
        }),
        optionId,
      );
      options.push({
        option: {
          ...buildTemplateMappedChartOptionBase({
            mapping: input.mapping,
            chartType,
            idSuffix: "mapped",
            metric: primaryMetric,
            dimension: pieDimension,
            group: null,
            aggregate: primaryMetric ? aggregate : "count",
          }),
          id: optionId,
          renderKind: null,
        },
        points,
      });
      continue;
    }

    if (!dimension) {
      continue;
    }

    if (
      metrics.length >= 2 &&
      (chartType === "bar" || chartType === "line") &&
      input.mapping.bucketPolicy === "date-month"
    ) {
      const optionId = `chart:${chartType}:${input.mapping.useCaseId}:mapped`;
      options.push({
        option: buildTemplateMappedChartOptionBase({
          mapping: input.mapping,
          chartType,
          idSuffix: "mapped",
          metric: metrics[0] ?? null,
          dimension,
          group: null,
          aggregate,
        }),
        points: buildMetricComparisonBuckets({
          rows: input.rows,
          dimension,
          metrics: metrics.slice(0, 3),
          chartType,
          optionId,
        }),
      });
      continue;
    }

    if (input.mapping.bucketPolicy === "numeric-bucket") {
      const dimensionKind = resolveNumericConceptBucketKind(dimension);
      if (!dimensionKind) {
        continue;
      }
      const optionId = `chart:${chartType}:${input.mapping.useCaseId}:mapped`;
      const option = buildTemplateMappedChartOptionBase({
        mapping: input.mapping,
        chartType,
        idSuffix: "mapped",
        metric: primaryMetric,
        dimension,
        group,
        aggregate: primaryMetric ? aggregate : "count",
      });
      options.push({
        option: {
          ...option,
          dimensionLabel: `${dimension.label} bracket`,
        },
        points: buildNumericConceptBucketPoints({
          rows: input.rows,
          dimension,
          dimensionKind,
          metric: primaryMetric,
          aggregate: primaryMetric ? aggregate : "count",
          chartType,
          optionId,
        }).map((point) => ({
          ...point,
          optionId,
        })),
      });
      continue;
    }

    if (group && primaryMetric) {
      const optionId = `chart:${chartType}:${input.mapping.useCaseId}:mapped`;
      options.push({
        option: buildTemplateMappedChartOptionBase({
          mapping: input.mapping,
          chartType,
          idSuffix: "mapped",
          metric: primaryMetric,
          dimension,
          group,
          aggregate,
        }),
        points: buildGroupedMetricBuckets({
          rows: input.rows,
          dimension,
          group,
          metric: primaryMetric,
          aggregate,
          chartType,
          optionId,
        }),
      });
      continue;
    }

    if (group && !primaryMetric) {
      const optionId = `chart:${chartType}:${input.mapping.useCaseId}:mapped`;
      options.push({
        option: buildTemplateMappedChartOptionBase({
          mapping: input.mapping,
          chartType,
          idSuffix: "mapped",
          metric: null,
          dimension,
          group,
          aggregate: "count",
        }),
        points: buildGroupedCountBuckets({
          rows: input.rows,
          dimension,
          group,
          chartType,
          optionId,
        }),
      });
      continue;
    }

    const optionId = `chart:${chartType}:${input.mapping.useCaseId}:mapped`;
    options.push({
      option: buildTemplateMappedChartOptionBase({
        mapping: input.mapping,
        chartType,
        idSuffix: "mapped",
        metric: primaryMetric,
        dimension,
        group: null,
        aggregate: primaryMetric ? aggregate : "count",
      }),
      points: buildDimensionBuckets({
        rows: input.rows,
        dimension,
        metric: primaryMetric,
        aggregate: primaryMetric ? aggregate : "count",
        chartType,
        optionId,
      }),
    });
  }

  return options;
};

const resolveFallbackReason = (input: {
  rows: readonly ReportGenerationVisualizationPolicyEngineRow[];
  columns: readonly ReportGenerationAdvancedVisualizationChartColumn[];
  numericColumns: readonly ReportGenerationAdvancedVisualizationChartColumn[];
  dateColumns: readonly ReportGenerationAdvancedVisualizationChartColumn[];
  categoryColumns: readonly ReportGenerationAdvancedVisualizationChartColumn[];
}): ReportGenerationVisualizationPolicyFallbackReason => {
  if (input.rows.length === 0) {
    return "no-preview-rows";
  }
  if (input.columns.length === 0) {
    return "no-selected-columns";
  }
  if (
    input.columns.every((column) => column.fieldType === "text") &&
    input.categoryColumns.length === 0
  ) {
    return "unsafe-text-only";
  }
  if (
    input.numericColumns.length === 0 &&
    input.dateColumns.length === 0 &&
    input.categoryColumns.length === 0
  ) {
    return "no-chartable-fields";
  }
  return "no-useful-chart-mapping";
};

const buildCoverageSignature = (input: {
  rows: readonly ReportGenerationVisualizationPolicyEngineRow[];
  columns: readonly ReportGenerationAdvancedVisualizationChartColumn[];
  numericColumns: readonly ReportGenerationAdvancedVisualizationChartColumn[];
  dateColumns: readonly ReportGenerationAdvancedVisualizationChartColumn[];
  categoryColumns: readonly ReportGenerationAdvancedVisualizationChartColumn[];
  options: readonly ReportGenerationAdvancedVisualizationChartOption[];
  matchedRuleIds: readonly ReportGenerationVisualizationPolicyRuleId[];
  fallbackReason: ReportGenerationVisualizationPolicyFallbackReason | null;
}): string =>
  [
    `rows:${input.rows.length}`,
    `columns:${input.columns.length}`,
    `numeric:${input.numericColumns.map((column) => column.fieldId).join(",") || "none"}`,
    `date:${input.dateColumns.map((column) => column.fieldId).join(",") || "none"}`,
    `category:${input.categoryColumns.map((column) => column.fieldId).join(",") || "none"}`,
    `options:${input.options.map((option) => `${option.id}:${option.chartType}`).join(",") || "none"}`,
    `rules:${input.matchedRuleIds.join(",") || "none"}`,
    `fallback:${input.fallbackReason ?? "none"}`,
  ].join("|");

const createVisualizationPolicy = (input: {
  matchedRuleIds: Iterable<ReportGenerationVisualizationPolicyRuleId>;
  options: readonly ReportGenerationAdvancedVisualizationChartOption[];
  defaultChartType: ReportGenerationAdvancedVisualizationChartType | null;
  defaultBodySurface: ReportGenerationAdvancedVisualizationBodySurface;
  fallbackReason: ReportGenerationVisualizationPolicyFallbackReason | null;
  coverageSignature: string;
}): ReportGenerationAdvancedVisualizationPolicy => {
  const availableChartTypes =
    REPORT_GENERATION_DEFAULT_CHART_TYPE_PRIORITY.filter((chartType) =>
      input.options.some((option) => option.chartType === chartType),
    );
  const defaultChartOptionId =
    input.defaultChartType === null
      ? null
      : (input.options.find(
          (option) => option.chartType === input.defaultChartType,
        )?.id ?? null);
  return {
    id: REPORT_GENERATION_VISUALIZATION_POLICY_ID,
    version: 1,
    defaultBodySurface: input.defaultBodySurface,
    defaultView: "chart",
    defaultChartType: input.defaultChartType,
    defaultChartOptionId,
    defaultChartTypePriority: [
      ...REPORT_GENERATION_DEFAULT_CHART_TYPE_PRIORITY,
    ],
    graphViewEnabled:
      REPORT_GENERATION_VISUALIZATION_POLICY_CONFIG.graphViewEnabled,
    graphDefaultAllowed: false,
    unmappedFallback: "table",
    fallbackReason: input.fallbackReason,
    matchedRuleIds: [...new Set(input.matchedRuleIds)].sort(),
    availableChartTypes,
    coverageSignature: input.coverageSignature,
  };
};

export const validateReportGenerationVisualizationPolicyResult = (
  result: ReportGenerationVisualizationPolicyResult,
): ReportGenerationVisualizationPolicyValidationIssue[] => {
  const issues: ReportGenerationVisualizationPolicyValidationIssue[] = [];
  const policy = result.policy;
  const chartOptionCount = result.chartOptions.length;
  const optionIds = new Set(result.chartOptions.map((option) => option.id));

  if (
    !REPORT_GENERATION_VISUALIZATION_POLICY_CONFIG.graphViewEnabled &&
    policy.graphViewEnabled
  ) {
    issues.push({
      code: "graph-view-disabled",
      message: "Run Reports graph visualization is disabled by policy.",
    });
  }

  if (policy.graphDefaultAllowed || policy.defaultView !== "chart") {
    issues.push({
      code: "graph-default-disallowed",
      message: "Run Reports visualization policy must never default to graph.",
    });
  }

  if (chartOptionCount === 0 && policy.defaultBodySurface !== "table") {
    issues.push({
      code: "missing-table-fallback",
      message:
        "Unmapped report datasets must fall back to the table body surface.",
    });
  }

  if (chartOptionCount === 0 && !policy.fallbackReason) {
    issues.push({
      code: "missing-table-fallback",
      message: "Table fallback policy results must include a fallback reason.",
    });
  }

  if (
    chartOptionCount > 0 &&
    policy.defaultBodySurface !== "visual" &&
    !policy.matchedRuleIds.includes("table-first-complex-selection")
  ) {
    issues.push({
      code: "incoherent-default-body-surface",
      message:
        "Mapped chart policy results must default to the visual body surface unless a table-first rule matched.",
    });
  }

  if (
    policy.defaultChartType !== null &&
    !result.chartOptions.some(
      (option) => option.chartType === policy.defaultChartType,
    )
  ) {
    issues.push({
      code: "incoherent-default-chart",
      message:
        "Policy default chart type must match an available chart option.",
    });
  }

  if (
    policy.defaultChartOptionId &&
    !optionIds.has(policy.defaultChartOptionId)
  ) {
    issues.push({
      code: "missing-chart-option",
      message:
        "Policy default chart option id must match an available chart option.",
    });
  }

  const availableChartTypes = new Set(
    result.chartOptions.map((option) => option.chartType),
  );
  if (
    policy.availableChartTypes.some(
      (chartType) => !availableChartTypes.has(chartType),
    )
  ) {
    issues.push({
      code: "incoherent-available-chart-types",
      message: "Policy available chart types must be backed by chart options.",
    });
  }

  for (const option of result.chartOptions) {
    const optionPoints = result.series.filter(
      (point) => point.optionId === option.id,
    );
    if (option.chartType === "pie" && !hasUsefulBucketShape(optionPoints)) {
      issues.push({
        code: "degenerate-pie-option",
        message: "Pie chart options must contain at least two useful buckets.",
      });
    }
    if (
      option.chartType === "scatter" &&
      optionPoints.filter((point) => point.x !== null && point.y !== null)
        .length < 2
    ) {
      issues.push({
        code: "degenerate-scatter-option",
        message:
          "Scatter chart options must contain row-level numeric pairs, not a single aggregate point.",
      });
    }
  }

  return issues;
};

export const resolveReportGenerationVisualizationPolicy = (
  input: ReportGenerationVisualizationPolicyEngineInput,
): ReportGenerationVisualizationPolicyResult => {
  const chartColumns = [...input.columns];
  const numericColumns = chartColumns.filter(
    (column) => column.fieldType === "number",
  );
  const dateColumns = chartColumns.filter(
    (column) => column.fieldType === "date",
  );
  const categoryColumns = chartColumns.filter(isCategoryColumn);
  const primaryMetric = numericColumns[0] ?? null;
  const secondaryMetric = numericColumns[1] ?? null;
  const primaryDimension = categoryColumns[0] ?? dateColumns[0] ?? null;
  const pieDimension =
    categoryColumns.find(
      (column) =>
        categoryDistinctBucketCount({ rows: input.rows, column }) >= 2,
    ) ??
    dateColumns.find(
      (column) =>
        new Set(
          input.rows
            .map((row) => readIsoMonthBucket(row.cells[column.fieldId]))
            .filter(Boolean),
        ).size >= 2,
    ) ??
    null;
  const primaryDate = dateColumns[0] ?? null;
  const primaryAggregate = chooseMetricAggregate(primaryMetric);
  const metricComparisonColumns = numericColumns.slice(0, 3);
  const categoryGroupConcept = chooseCategoryGroupConcept({
    rows: input.rows,
    categoryColumns,
  });
  const groupDimension =
    GROUP_DIMENSION_FIELD_IDS.map((fieldId) =>
      categoryColumns.find((column) => column.fieldId === fieldId),
    ).find(
      (column): column is ReportGenerationAdvancedVisualizationChartColumn =>
        Boolean(column),
    ) ??
    categoryColumns[0] ??
    null;
  const numericConceptDimension = chooseNumericConceptDimension({
    rows: input.rows,
    numericColumns,
  });
  const numericConceptMetric = numericConceptDimension
    ? chooseNumericConceptMetric({
        rows: input.rows,
        numericColumns,
        dimension: numericConceptDimension.column,
      })
    : null;
  const preferredTimelineDimension = primaryDate ?? primaryDimension;
  const selectedFieldSignature =
    input.selectedFieldSignature ??
    chartColumns.map((column) => column.fieldId).join("|");
  const filterSignature = input.filterSignature;
  const templateMapping =
    resolveRunReportsVisualizationMappingByTemplateId(input.templateId) ??
    inferRunReportsVisualizationMappingFromFields(
      chartColumns.map((column) => column.fieldId),
    );
  const options: ReportGenerationAdvancedVisualizationChartOption[] = [];
  const series: ReportGenerationAdvancedVisualizationChartPoint[] = [];
  const matchedRuleIds = new Set<ReportGenerationVisualizationPolicyRuleId>();
  const addOption = (
    ruleId: ReportGenerationVisualizationPolicyRuleId,
    option: Omit<
      ReportGenerationAdvancedVisualizationChartOption,
      "pointCount"
    >,
    points: ReportGenerationAdvancedVisualizationChartPoint[],
  ) => {
    if (
      templateMapping?.primarySurface === "table" &&
      ruleId !== "template-visualization-mapping"
    ) {
      return;
    }
    if (points.length === 0) {
      return;
    }
    if (option.chartType === "pie" && !hasUsefulBucketShape(points)) {
      matchedRuleIds.add("degenerate-pie-rejected");
      return;
    }
    if (option.chartType === "scatter" && !hasUsefulScatterShape(points)) {
      matchedRuleIds.add("degenerate-scatter-rejected");
      return;
    }
    if (
      option.chartType !== "pie" &&
      option.chartType !== "scatter" &&
      !hasUsefulSingleSeriesShape(points)
    ) {
      matchedRuleIds.add("degenerate-single-point-rejected");
      return;
    }
    matchedRuleIds.add(ruleId);
    options.push({ ...option, pointCount: points.length });
    series.push(...points);
  };

  if (templateMapping) {
    matchedRuleIds.add("template-visualization-mapping");
    for (const mappedOption of buildTemplateMappedOptions({
      mapping: templateMapping,
      rows: input.rows,
      columns: chartColumns,
    })) {
      addOption(
        "template-visualization-mapping",
        mappedOption.option,
        mappedOption.points,
      );
    }
  }

  if (preferredTimelineDimension && metricComparisonColumns.length >= 2) {
    const useCaseId = metricComparisonColumns.some(
      (column) => column.fieldId === "debit" || column.fieldId === "credit",
    )
      ? "tuition-cashflow"
      : metricComparisonColumns.some(
            (column) => column.fieldId === "contract_amount",
          )
        ? "contract-value"
        : "aging-pressure";
    const comparisonLabel =
      useCaseId === "tuition-cashflow"
        ? `Tuition cashflow by ${preferredTimelineDimension.label}`
        : useCaseId === "contract-value"
          ? `Contract value by ${preferredTimelineDimension.label}`
          : `Aging pressure by ${preferredTimelineDimension.label}`;
    const groupedOptionId = `chart:bar:${useCaseId}`;
    const groupedPoints = buildMetricComparisonBuckets({
      rows: input.rows,
      dimension: preferredTimelineDimension,
      metrics: metricComparisonColumns,
      chartType: "bar",
      optionId: groupedOptionId,
    });
    addOption(
      "multi-metric-timeline",
      {
        id: groupedOptionId,
        chartType: "bar",
        renderKind: "grouped-bar",
        useCaseId,
        label: comparisonLabel,
        aggregate: "sum",
        metricFieldId: metricComparisonColumns[0]?.fieldId ?? null,
        metricLabel: metricComparisonColumns
          .map((column) => column.label)
          .join(" / "),
        dimensionFieldId: preferredTimelineDimension.fieldId,
        dimensionLabel: preferredTimelineDimension.label,
        animationVariant: "staggered",
        animationLabel: "Staggered reveal",
      },
      groupedPoints,
    );

    const ribbonOptionId = `chart:line:${useCaseId}`;
    const ribbonPoints = buildMetricComparisonBuckets({
      rows: input.rows,
      dimension: preferredTimelineDimension,
      metrics: metricComparisonColumns,
      chartType: "line",
      optionId: ribbonOptionId,
    });
    addOption(
      "multi-metric-timeline",
      {
        id: ribbonOptionId,
        chartType: "line",
        renderKind: "ribbon-line",
        useCaseId,
        label: `${comparisonLabel} trend`,
        aggregate: "sum",
        metricFieldId: metricComparisonColumns[0]?.fieldId ?? null,
        metricLabel: metricComparisonColumns
          .map((column) => column.label)
          .join(" / "),
        dimensionFieldId: preferredTimelineDimension.fieldId,
        dimensionLabel: preferredTimelineDimension.label,
        animationVariant: "smooth",
        animationLabel: "Smooth reveal",
      },
      ribbonPoints,
    );
  }

  const tuitionPriorYearOverlay = buildTuitionPriorYearOverlayBuckets({
    rows: input.rows,
    columns: chartColumns,
    primaryDate,
  });
  if (tuitionPriorYearOverlay) {
    addOption(
      "tuition-prior-year-overlay",
      {
        id: "chart:bar:tuition-outstanding-prior-year",
        chartType: "bar",
        renderKind: "grouped-bar",
        useCaseId: "tuition-outstanding-prior-year",
        label: `Outstanding tuition due by month: ${tuitionPriorYearOverlay.currentYear} over ${tuitionPriorYearOverlay.priorYear}`,
        aggregate: "sum",
        metricFieldId: tuitionPriorYearOverlay.metric.fieldId,
        metricLabel: tuitionPriorYearOverlay.metric.label,
        dimensionFieldId: primaryDate?.fieldId ?? "due_date",
        dimensionLabel: primaryDate?.label ?? "Due month",
        groupFieldId: "academic_year",
        groupLabel: "Academic year",
        animationVariant: "staggered",
        animationLabel: "Staggered reveal",
      },
      tuitionPriorYearOverlay.barPoints,
    );
    addOption(
      "tuition-prior-year-overlay",
      {
        id: "chart:line:tuition-outstanding-prior-year",
        chartType: "line",
        renderKind: "ribbon-line",
        useCaseId: "tuition-outstanding-prior-year",
        label: `Outstanding tuition due trend: ${tuitionPriorYearOverlay.currentYear} over ${tuitionPriorYearOverlay.priorYear}`,
        aggregate: "sum",
        metricFieldId: tuitionPriorYearOverlay.metric.fieldId,
        metricLabel: tuitionPriorYearOverlay.metric.label,
        dimensionFieldId: primaryDate?.fieldId ?? "due_date",
        dimensionLabel: primaryDate?.label ?? "Due month",
        groupFieldId: "academic_year",
        groupLabel: "Academic year",
        animationVariant: "smooth",
        animationLabel: "Smooth reveal",
      },
      tuitionPriorYearOverlay.linePoints,
    );
  }

  if (
    primaryDate &&
    groupDimension &&
    primaryMetric &&
    groupDimension.fieldId !== primaryDate.fieldId &&
    (metricComparisonColumns.length < 2 ||
      primaryMetric.fieldId === "payment_amount")
  ) {
    const useCaseId =
      primaryMetric.fieldId === "payment_amount"
        ? "collections-mix"
        : `${normalizeKeyPart(primaryMetric.fieldId)}-${normalizeKeyPart(groupDimension.fieldId)}`;
    const groupedOptionId = `chart:bar:${useCaseId}`;
    const groupedPoints = buildGroupedMetricBuckets({
      rows: input.rows,
      dimension: primaryDate,
      group: groupDimension,
      metric: primaryMetric,
      aggregate: primaryAggregate,
      chartType: "bar",
      optionId: groupedOptionId,
    });
    if (new Set(groupedPoints.map((point) => point.seriesId)).size > 1) {
      addOption(
        "date-group-metric",
        {
          id: groupedOptionId,
          chartType: "bar",
          renderKind: "grouped-bar",
          useCaseId,
          label: `${primaryMetric.label} by ${primaryDate.label} and ${groupDimension.label}`,
          aggregate: primaryAggregate,
          metricFieldId: primaryMetric.fieldId,
          metricLabel: primaryMetric.label,
          dimensionFieldId: primaryDate.fieldId,
          dimensionLabel: primaryDate.label,
          groupFieldId: groupDimension.fieldId,
          groupLabel: groupDimension.label,
          animationVariant: "staggered",
          animationLabel: "Staggered reveal",
        },
        groupedPoints,
      );
    }
  }

  if (categoryGroupConcept) {
    const useCaseId = primaryMetric
      ? `${normalizeKeyPart(primaryMetric.fieldId)}-${normalizeKeyPart(categoryGroupConcept.bucket.fieldId)}-${normalizeKeyPart(categoryGroupConcept.group.fieldId)}`
      : `${normalizeKeyPart(categoryGroupConcept.bucket.fieldId)}-${normalizeKeyPart(categoryGroupConcept.group.fieldId)}-count`;
    const optionId = `chart:bar:${useCaseId}`;
    const points = primaryMetric
      ? buildGroupedMetricBuckets({
          rows: input.rows,
          dimension: categoryGroupConcept.bucket,
          group: categoryGroupConcept.group,
          metric: primaryMetric,
          aggregate: primaryAggregate,
          chartType: "bar",
          optionId,
        })
      : buildGroupedCountBuckets({
          rows: input.rows,
          dimension: categoryGroupConcept.bucket,
          group: categoryGroupConcept.group,
          chartType: "bar",
          optionId,
        });
    addOption(
      "category-group-bar",
      {
        id: optionId,
        chartType: "bar",
        renderKind: "grouped-bar",
        useCaseId,
        label: primaryMetric
          ? `${primaryMetric.label} by ${categoryGroupConcept.bucket.label} and ${categoryGroupConcept.group.label}`
          : `Rows by ${categoryGroupConcept.bucket.label} and ${categoryGroupConcept.group.label}`,
        aggregate: primaryMetric ? primaryAggregate : "count",
        metricFieldId: primaryMetric?.fieldId ?? null,
        metricLabel: primaryMetric?.label ?? "Rows",
        dimensionFieldId: categoryGroupConcept.bucket.fieldId,
        dimensionLabel: categoryGroupConcept.bucket.label,
        groupFieldId: categoryGroupConcept.group.fieldId,
        groupLabel: categoryGroupConcept.group.label,
        animationVariant: "staggered",
        animationLabel: "Staggered reveal",
      },
      points,
    );
  }

  const hasExplicitDimension = Boolean(primaryDimension);
  if (
    !hasExplicitDimension &&
    numericConceptDimension &&
    numericColumns.length >= 2
  ) {
    const conceptDimension = numericConceptDimension.column;
    let conceptMetric = numericConceptMetric;
    let conceptAggregate: "sum" | "average" | "count" = conceptMetric
      ? chooseMetricAggregate(conceptMetric)
      : "count";
    const conceptOptionId = "chart:bar:numeric-concept-bucket";
    let conceptPoints = buildNumericConceptBucketPoints({
      rows: input.rows,
      dimension: conceptDimension,
      dimensionKind: numericConceptDimension.kind,
      metric: conceptMetric,
      aggregate: conceptAggregate,
      chartType: "bar",
      optionId: conceptOptionId,
    });
    if (conceptMetric && conceptPoints.length < 2) {
      conceptMetric = null;
      conceptAggregate = "count";
      conceptPoints = buildNumericConceptBucketPoints({
        rows: input.rows,
        dimension: conceptDimension,
        dimensionKind: numericConceptDimension.kind,
        metric: null,
        aggregate: conceptAggregate,
        chartType: "bar",
        optionId: conceptOptionId,
      });
    }
    addOption(
      "numeric-concept-bucket-bar",
      {
        id: conceptOptionId,
        chartType: "bar",
        useCaseId: `${normalizeKeyPart(conceptDimension.fieldId)}-bucket`,
        label: conceptMetric
          ? `${conceptMetric.label} by ${conceptDimension.label} bracket`
          : `Rows by ${conceptDimension.label} bracket`,
        aggregate: conceptAggregate,
        metricFieldId: conceptMetric?.fieldId ?? null,
        metricLabel: conceptMetric?.label ?? "Rows",
        dimensionFieldId: conceptDimension.fieldId,
        dimensionLabel: `${conceptDimension.label} bracket`,
        animationVariant: "staggered",
        animationLabel: "Staggered reveal",
      },
      conceptPoints,
    );
  }

  if (primaryDimension || primaryMetric) {
    const barOptionId = "chart:bar:primary";
    const shouldSuppressAllRowsMetricTotal =
      !primaryDimension &&
      matchedRuleIds.has("numeric-concept-bucket-bar") &&
      options.some(
        (option) => option.id === "chart:bar:numeric-concept-bucket",
      );
    if (!shouldSuppressAllRowsMetricTotal) {
      const barPoints = buildDimensionBuckets({
        rows: input.rows,
        dimension: primaryDimension,
        metric: primaryMetric,
        aggregate: primaryMetric ? primaryAggregate : "count",
        chartType: "bar",
        optionId: barOptionId,
      });
      addOption(
        primaryDimension ? "primary-dimension-bar" : "metric-total-bar",
        {
          id: barOptionId,
          chartType: "bar",
          label: primaryDimension
            ? `${primaryMetric ? primaryMetric.label : "Rows"} by ${primaryDimension.label}`
            : `${primaryMetric ? primaryMetric.label : "Rows"} aggregate`,
          aggregate: primaryMetric ? primaryAggregate : "count",
          metricFieldId: primaryMetric?.fieldId ?? null,
          metricLabel: primaryMetric?.label ?? null,
          dimensionFieldId: primaryDimension?.fieldId ?? null,
          dimensionLabel: primaryDimension?.label ?? null,
        },
        barPoints,
      );
    }
  }

  if (primaryDate && primaryMetric) {
    const lineOptionId = "chart:line:date-primary";
    const linePoints = buildDimensionBuckets({
      rows: input.rows,
      dimension: primaryDate,
      metric: primaryMetric,
      aggregate: primaryAggregate,
      chartType: "line",
      optionId: lineOptionId,
    });
    addOption(
      "date-metric-line",
      {
        id: lineOptionId,
        chartType: "line",
        label: `${primaryMetric.label} by ${primaryDate.label}`,
        aggregate: primaryAggregate,
        metricFieldId: primaryMetric.fieldId,
        metricLabel: primaryMetric.label,
        dimensionFieldId: primaryDate.fieldId,
        dimensionLabel: primaryDate.label,
      },
      linePoints,
    );
  }

  if (pieDimension) {
    const pieOptionId = "chart:pie:primary";
    const piePoints = capPiePoints(
      buildDimensionBuckets({
        rows: input.rows,
        dimension: pieDimension,
        metric: primaryMetric,
        aggregate: primaryMetric ? primaryAggregate : "count",
        chartType: "pie",
        optionId: pieOptionId,
      }),
      pieOptionId,
    );
    addOption(
      "category-share-pie",
      {
        id: pieOptionId,
        chartType: "pie",
        label: primaryMetric
          ? `${primaryMetric.label} share`
          : `${pieDimension.label} count`,
        aggregate: primaryMetric ? primaryAggregate : "count",
        metricFieldId: primaryMetric?.fieldId ?? null,
        metricLabel: primaryMetric?.label ?? null,
        dimensionFieldId: pieDimension.fieldId,
        dimensionLabel: pieDimension.label,
      },
      piePoints,
    );
  }

  if (primaryMetric && secondaryMetric) {
    const scatterOptionId = "chart:scatter:numeric-pair";
    const scatterPoints = buildRowLevelScatterPoints({
      rows: input.rows,
      xMetric: primaryMetric,
      yMetric: secondaryMetric,
      group: primaryDimension,
      optionId: scatterOptionId,
    });
    addOption(
      "numeric-pair-scatter",
      {
        id: scatterOptionId,
        chartType: "scatter",
        label: `${primaryMetric.label} vs ${secondaryMetric.label}`,
        aggregate: "average",
        xFieldId: primaryMetric.fieldId,
        xLabel: primaryMetric.label,
        yFieldId: secondaryMetric.fieldId,
        yLabel: secondaryMetric.label,
        dimensionFieldId: primaryDimension?.fieldId ?? null,
        dimensionLabel: primaryDimension?.label ?? null,
      },
      scatterPoints,
    );
  }

  const mappedDefaultChartType =
    templateMapping?.chartTypes.find((chartType) =>
      options.some((option) => option.chartType === chartType),
    ) ?? null;
  const defaultChartType =
    mappedDefaultChartType ??
    REPORT_GENERATION_DEFAULT_CHART_TYPE_PRIORITY.find((chartType) =>
      options.some((option) => option.chartType === chartType),
    ) ??
    null;
  const tableFirstComplexSelection = shouldUseTableFirstForComplexSelection({
    rows: input.rows,
    columns: chartColumns,
    entityLinks: input.entityLinks,
    categoryColumns,
  });
  if (tableFirstComplexSelection) {
    matchedRuleIds.add("table-first-complex-selection");
  }
  const finalMatchedRuleIds = [...matchedRuleIds].sort();
  const fallbackReason =
    options.length > 0
      ? null
      : resolveFallbackReason({
          rows: input.rows,
          columns: chartColumns,
          numericColumns,
          dateColumns,
          categoryColumns,
        });
  const coverageSignature = buildCoverageSignature({
    rows: input.rows,
    columns: chartColumns,
    numericColumns,
    dateColumns,
    categoryColumns,
    options,
    matchedRuleIds: finalMatchedRuleIds,
    fallbackReason,
  });
  const defaultBodySurface: ReportGenerationAdvancedVisualizationBodySurface =
    options.length > 0 && !tableFirstComplexSelection ? "visual" : "table";
  const policy = createVisualizationPolicy({
    matchedRuleIds: finalMatchedRuleIds,
    options,
    defaultChartType,
    defaultBodySurface,
    fallbackReason,
    coverageSignature,
  });

  const warningSet = sortedUnique([
    ...(input.warnings ?? []),
    ...(chartColumns.some(
      (column) =>
        column.fieldType === "text" && !isSafeTextCategoryColumn(column),
    )
      ? [
          "Sensitive text columns are excluded from chart labels and only contribute to aggregate counts.",
        ]
      : []),
  ]);

  const result: ReportGenerationVisualizationPolicyResult = {
    columns: chartColumns,
    entityLinks: [...input.entityLinks],
    summary: {
      selectedRowCount: input.rows.length,
      visibleColumnCount: chartColumns.length,
      numericFieldCount: numericColumns.length,
      dateFieldCount: dateColumns.length,
      categoryFieldCount: categoryColumns.length,
      selectedFieldSignature,
      filterSignature,
      warnings: warningSet,
    },
    chartOptions: options,
    defaultChartType,
    policy,
    series,
  };
  const validationIssues =
    validateReportGenerationVisualizationPolicyResult(result);
  if (validationIssues.length > 0) {
    return {
      ...result,
      summary: {
        ...result.summary,
        warnings: sortedUnique([
          ...result.summary.warnings,
          ...validationIssues.map((issue) => issue.message),
        ]),
      },
    };
  }
  return result;
};
